import * as runtime from '../runtime';
import { BaseAPI } from '../baseApi';
import { Activity, SubtaskItem } from '@api';
import { AIProfile } from '@/contexts/AIProfileContext';
import { HttpStatusCode } from 'axios';
import { intervalType } from '@/contexts/StatisticsContext';
/* tslint:disable */
/* eslint-disable */

export type Suggestion = {
  activityName: string;
  startDate: string;
  duration: number;
  description: string;
  subtasks?: SubtaskItem[];
};

export type languageKey = 'sv' | 'en' | 'de' | 'fr' | 'es' | 'it' | 'pl' | 'nb' | 'da' | 'fi';

const LanguageMapper: Record<languageKey, string> = {
  sv: 'Svenska',
  en: 'Engelska',
  de: 'Tyska',
  fr: 'Franska',
  es: 'Spanska',
  it: 'Italienska',
  pl: 'Polska',
  nb: 'Norska',
  da: 'Danska',
  fi: 'Finska',
};

export class AiApi extends BaseAPI {
  // Rensa cachen
  clearSuggestionCache = () => {
    localStorage.removeItem('suggestionCache');
    localStorage.removeItem('suggestionCache_exp');
    localStorage.removeItem('suggestionCache_language');
  };

  // Spara data i cachen
  saveSuggestionCache = (data: string, language: languageKey) => {
    const suggestionCacheTimeLimit = 60 * 60 * 1000;
    localStorage.setItem('suggestionCache', JSON.stringify(data));
    localStorage.setItem('suggestionCache_exp', (Date.now() + suggestionCacheTimeLimit).toString());
    localStorage.setItem('suggestionCache_language', language);
  };

  getSuggestionCache = (language: languageKey) => {
    const cacheLanguage = localStorage.getItem('suggestionCache_language');
    const expiration = localStorage.getItem('suggestionCache_exp');
    if (expiration && Date.now() < parseInt(expiration, 10) && cacheLanguage === language) {
      const data = localStorage.getItem('suggestionCache');
      return data ? JSON.parse(data) : null;
    }
    // Rensa cache om den är för gammal
    this.clearSuggestionCache();
    return null;
  };

  /**
   * Plockar ut AI-svarets textinnehåll, eller null om svaret inte bär något.
   *
   * Backend svarar 204 med tom body när ingen giltig AI-nyckel är konfigurerad — då finns
   * inget `choices` och det är INTE ett fel, bara "AI av". Utan den här skillnaden kastade
   * varje metod på `response.data.choices[0]` och fyllde konsolen med fel som bara betyder
   * att dev-miljön kör utan nyckel. Ett riktigt fel (nyckel finns men fungerar inte) ger
   * fortfarande 500 och går samma väg som förut.
   */
  private extractAiContent = (response: { data?: any }): string | null => {
    const content = response?.data?.choices?.[0]?.message?.content;
    return typeof content === 'string' ? content : null;
  };

  activitySuggestions = async (
    language: languageKey,
    suggestionSpecification?: string,
    refresh = false,
  ): Promise<Suggestion[]> => {
    const cachedData = this.getSuggestionCache(language);

    const oldActivities: Suggestion[] = [];

    if (cachedData) {
      try {
        const parsedCache: Suggestion[] = JSON.parse(cachedData);
        if (Array.isArray(parsedCache) && parsedCache.length === 3) {
          if (!refresh) return parsedCache;
          else oldActivities.push(...parsedCache);
        } else this.clearSuggestionCache();
      } catch (e) {
        console.error('Error parsing cached data:', e);
        // There might be something wrong with the cache, clear it
        this.clearSuggestionCache();
      }
    }

    const previousSuggestions = JSON.stringify(oldActivities);

    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const queryString = suggestionSpecification ? `/${suggestionSpecification}` : '';
      const response = await this.request({
        path: `/ai/suggestions/${LanguageMapper[language] + queryString}`,
        method: 'POST',
        headers: headerParameters,
        body: previousSuggestions,
      });

      const responseData = this.extractAiContent(response);
      // AI av (204): inga förslag att visa, och inget att cacha eller klaga på.
      if (responseData === null) return [];
      this.saveSuggestionCache(responseData, language);

      const parsedResponse = JSON.parse(responseData);
      if (Array.isArray(parsedResponse) && parsedResponse.length === 3) {
        return parsedResponse;
      } else {
        console.error('Invalid response from AI:', responseData);
        return [];
      }
    } catch (error) {
      // AI-fel kastas ALDRIG vidare. Utan nyckel i miljön svarar backenden 500 på varje
      // anrop, och det här anropet görs i en useEffect utan catch (TaskCreated) — ett
      // kastat fel blev därför en obehandlad promise-rejection och en kraschskärm ovanpå
      // en aktivitet som faktiskt hade skapats. Tom lista är samma svar som "AI gav inga
      // förslag", vilket varje anropare redan hanterar.
      console.error('Error fetching suggestions:', error);
      return [];
    }
  };

  activityDateTimeSuggestion = async (
    language: languageKey,
    activityTitle: string,
  ): Promise<{ startDate: string; suggestionText: string } | null> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: `/ai/suggestions/datetime/${LanguageMapper[language]}/${activityTitle}`,
        method: 'GET',
        headers: headerParameters,
      });

      const responseData = this.extractAiContent(response);
      // AI av (204): inget förslag. Callarna har redan en "inget förslag"-gren.
      if (responseData === null) return null;

      const parsedResponse = JSON.parse(responseData);
      if (parsedResponse) {
        return {
          startDate: parsedResponse.startDate,
          suggestionText: parsedResponse.suggestionText,
        };
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      // Se activitySuggestions: AI-fel blir "inget förslag", aldrig ett kastat fel.
      console.error('Error fetching AI suggestion:', error);
      return null;
    }
  };

  activityDurationSuggestion = async (
    language: languageKey,
    activity: Activity,
  ): Promise<{ duration: number; suggestionText: string } | null> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: `/ai/suggestions/duration/${LanguageMapper[language]}`,
        method: 'POST',
        headers: headerParameters,
        body: runtime.toRequestBody(activity),
      });

      const responseData = this.extractAiContent(response);
      // AI av (204): inget förslag. Callarna har redan en "inget förslag"-gren.
      if (responseData === null) return null;

      const parsedResponse = JSON.parse(responseData);
      if (parsedResponse) {
        return {
          duration: parsedResponse.duration,
          suggestionText: parsedResponse.suggestionText,
        };
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      // Se activitySuggestions: AI-fel blir "inget förslag", aldrig ett kastat fel.
      console.error('Error fetching AI suggestion:', error);
      return null;
    }
  };

  activityDetailsSuggestion = async (
    language: languageKey,
    activity: Activity,
  ): Promise<{ description: string; subtasks: SubtaskItem[] } | null> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: `/ai/suggestions/details/${LanguageMapper[language]}`,
        method: 'POST',
        headers: headerParameters,
        body: runtime.toRequestBody(activity),
      });

      const responseData = this.extractAiContent(response);
      // AI av (204): inget förslag. Callarna har redan en "inget förslag"-gren.
      if (responseData === null) return null;

      const parsedResponse = JSON.parse(responseData);
      if (parsedResponse) {
        return {
          description: parsedResponse.description,
          subtasks: parsedResponse.subtasks,
        };
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      // Se activitySuggestions: AI-fel blir "inget förslag", aldrig ett kastat fel.
      console.error('Error fetching AI suggestion:', error);
      return null;
    }
  };

  /**
   * AI-sammanfattningen av statistiken för perioden som visas. Med supervisedTag gäller den
   * brukaren; utan gällde den förut alltid den inloggade, även i handledarläget.
   */
  statisticsAnalysis = async (
    interval: intervalType,
    language: languageKey,
    period: { start: string; end: string },
    supervisedTag?: string,
  ): Promise<string> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };
    const queryParameters: any = {
      intervalStartDate: period.start,
      intervalEndDate: period.end,
    };
    if (supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/ai/statistics/${interval}/${LanguageMapper[language ?? 'sv']}`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      });

      return this.extractAiContent(response) ?? '';
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return '';
    }
  };

  generateImage = async (languge: languageKey, description: string): Promise<File | null> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: `/ai/image/${LanguageMapper[languge]}`,
        method: 'POST',
        headers: headerParameters,
        body: JSON.stringify({ description }),
      });

      const base64Data = response.data; // Assuming `response.data` is base64
      // AI av (204): tom body. Utan den här kontrollen blev det en 0-bytes PNG i stället
      // för ett tydligt "ingen bild".
      if (typeof base64Data !== 'string' || base64Data.length === 0) return null;

      // Convert base64 string to Blob directly
      const blob = await (await fetch(`data:image/png;base64,${base64Data}`)).blob();

      // Create a File from the Blob
      const file = new File([blob], 'generated-image.png', { type: 'image/png' });

      return file;
    } catch (error) {
      // UNDANTAGET från regeln ovan, och med flit: anroparen (GenerateImageButton) läser
      // felmeddelandet för att skilja ut takgränsen "You have reached the limit of 15
      // generated images" och visa den för användaren. Sväljs felet här försvinner den
      // besked-vägen, och barnet får i stället en knapp som tyst inte gör något.
      // Knappen har redan en egen try/catch, så inget fel når användaren som en krasch.
      console.error('Error when generating image:', error);
      throw error;
    }
  };

  createAIProfile = async (
    aiProfile: AIProfile,
  ): Promise<AIProfile | runtime.ResponseError | undefined> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: '/ai/profile',
        method: 'POST',
        headers: headerParameters,
        body: JSON.stringify(aiProfile),
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): AIProfile => jsonValue,
      ).value();
    } catch (error) {
      console.error('Error creating AI profile:', error);
      if (error instanceof runtime.ResponseError) {
        return error;
      }
    }
  };

  getAIProfile = async (): Promise<AIProfile | undefined> => {
    try {
      const response = await this.request({
        path: '/ai/profile',
        method: 'GET',
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): AIProfile => jsonValue,
      ).value();
    } catch (error) {
      console.error('Error getting AI profile:', error);
    }
  };

  updateAIProfile = async (
    aiProfile: AIProfile,
  ): Promise<HttpStatusCode | runtime.ResponseError | undefined> => {
    const headerParameters: runtime.HTTPHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await this.request({
        path: '/ai/profile',
        method: 'PUT',
        headers: headerParameters,
        body: JSON.stringify(aiProfile),
      });

      return response.status;
    } catch (error) {
      console.error('Error updating AI profile:', error);
      if (error instanceof runtime.ResponseError) {
        return error;
      }
    }
  };

  deleteAIProfile = async (): Promise<HttpStatusCode | undefined> => {
    try {
      const response = await this.request({
        path: '/ai/profile',
        method: 'DELETE',
      });
      return response.status;
    } catch (error) {
      console.error('Error deleting AI profile:', error);
    }
  };
}
